import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from '../api/axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    
    const handleGlobalLogout = () => {
      logout();
    };
    window.addEventListener('auth_logout', handleGlobalLogout);
    return () => window.removeEventListener('auth_logout', handleGlobalLogout);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await axios.post('/auth/login', { email, password });
    const { token, refreshToken, user } = response.data;
    localStorage.setItem('token', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    setUser(user);

    // Redirect based on role after login
    if (user.role === 'admin') {
      window.location.href = '/admin/engineer-requests';
    } else if (user.role === 'engineer') {
      window.location.href = '/engineer/dashboard';
    } else if (user.role === 'customer') {
      window.location.href = '/customer/dashboard';
    }

    return user;
  };

  // role is intentionally not a parameter here — the backend's /auth/register
  // endpoint ignores any client-supplied role and always creates a plain
  // 'customer' account (see backend/controllers/auth.js). Elevated roles
  // are only ever granted server-side via the engineer approval flow.
  //
  // register() no longer auto-logs in. It calls the backend which sends an
  // OTP to the user's email. The caller should check response.requiresVerification
  // and prompt the user for the code. Use verifyEmail() to complete the flow.
  const register = async (name, email, password) => {
    const response = await axios.post('/auth/register', { name, email, password });
    return response.data; // { requiresVerification: true, email, message }
  };

  /**
   * Verify the email OTP entered by the user after registration.
   * On success, stores the token and sets the user — completing the login.
   * @param {string} email
   * @param {string} otp - 6-digit code from the verification email
   */
  const verifyEmail = async (email, otp) => {
    const response = await axios.post('/auth/verify-email', { email, otp });
    const { token, refreshToken, user } = response.data;
    localStorage.setItem('token', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    setUser(user);
    return user;
  };

  /**
   * Resend the verification OTP to the given email.
   * The server regenerates and re-sends the code; the existing OTP is invalidated.
   */
  const resendOtp = async (email) => {
    const response = await axios.post('/auth/resend-otp', { email });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const updateUser = async (updates) => {
    const response = await axios.put('/auth/update', updates);
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    login,
    register,
    verifyEmail,
    resendOtp,
    logout,
    updateUser,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};